import { useCallback } from 'react';

import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';

import type { DaDataParty, DaDataSuggestion } from '@pulse-web-ui/dadata';
import { HelperText } from '@pulse-web-ui/helper-text';

import {
  DaDataPartySuggestion,
  type DadataPartySuggestionsProps,
} from '@src/entities';

import { addTestAttribute } from '@shared/utils';

type DaDataSuggestionValue = DaDataSuggestion<DaDataParty>;

type DaDataPartySuggestionControllerProps<T extends FieldValues> =
  UseControllerProps<T> &
    DadataPartySuggestionsProps & {
      label: string;
      testId?: string;
    };

export const DaDataPartySuggestionController = <T extends FieldValues>({
  label,
  disabled,
  onChange,
  filterOkved,
  testId = 'dadataParty',
  ...controllerProps
}: DaDataPartySuggestionControllerProps<T>) => {
  const { field, fieldState } = useController(controllerProps);

  const onDaDataPartyChange = useCallback(
    (data?: DaDataSuggestionValue) => {
      if (data) {
        field.onChange(data?.value);
        onChange(data);
      }
    },
    [field]
  );

  return (
    <HelperText
      status={!!fieldState.error ? 'error' : 'default'}
      message={fieldState.error?.message}
    >
      <DaDataPartySuggestion
        value={{ value: field.value || '' } as DaDataSuggestionValue}
        onChange={onDaDataPartyChange}
        filterOkved={filterOkved}
        inputProps={{
          ...field,
          value: field.value,
          disabled,
          error: !!fieldState.error,
          label,
          autoComplete: 'nope',
          ...addTestAttribute(testId),
        }}
      />
    </HelperText>
  );
};
